import { useQuery } from '@tanstack/react-query'
import { v2GetPortfolioOutreachStatusOptions } from '@workspace/api-backend/react-query'
import { useOverviewQuery } from '../../../hooks/api'
import { useStartupFeatureFlagsQuery } from '../../feature-flags/hooks/use-startup-feature-flags'
import { isLinkedInConnected } from '../components/connect-linkedin-action'
import type { LaunchStepId } from '../config/launch-checklist'
import { PROFILE_COMPLETE_THRESHOLD } from '../config/launch-checklist'
import type { OutreachStatusData } from '../types'

export interface LaunchStep {
  id: LaunchStepId
  label: string
  done: boolean
}

export interface LaunchChecklist {
  steps: LaunchStep[]
  completedCount: number
  allComplete: boolean
  /** First incomplete step — drives the dashboard's single primary CTA. */
  firstIncomplete: LaunchStep | null
  /** Profile completeness percent (for the profile step's progress pill). */
  profilePercent: number
  /** First missing profile check (its `?focus=` key), so the profile step opens on it. */
  profileFocus: string | null
  /**
   * Whether the backend launch mutation can actually be called. Launch is only
   * valid at AWAITING_CAMPAIGN (LinkedIn connected) and not yet approved — the
   * checkmarks alone don't guarantee this.
   */
  canCallLaunchMutation: boolean
}

// Derives the launch-checklist conditions from already-cached queries. The
// outreach status is passed in (single-sourced from the route's status query) so
// this hook never opens a second poll on the same endpoint. Top-up is no longer
// a launch step — the free trial covers the first month.
export function useOutreachLaunchChecklist(
  startupId: string,
  outreachStatus: OutreachStatusData | null
): LaunchChecklist {
  const overviewQuery = useOverviewQuery(startupId)
  const flagsQuery = useStartupFeatureFlagsQuery(startupId)
  const portfolioStatusQuery = useQuery(
    v2GetPortfolioOutreachStatusOptions({ path: { id: startupId } })
  )
  const portfolio =
    portfolioStatusQuery.data?.status === 'ok' ? portfolioStatusQuery.data.data : null

  const profileCompleteness = overviewQuery.data?.data?.profileCompleteness
  const profilePercent = profileCompleteness?.percentage ?? 0
  const profileFocus =
    profileCompleteness?.nextSteps.find((step) => step.link === '/profile')?.key ?? null

  // Both channels run by default now (no "Choose campaigns" gate). Each channel
  // that's part of the campaign must be reviewed before the step is done:
  // investor unless explicitly turned off, and portfolio whenever its feature is
  // on (its enabled flag only exists post-provisioning, so pre-launch we require
  // its review by default — matching "both channels run by default").
  const portfolioAvailable = flagsQuery.data?.portfolioOutreach ?? false
  const investorSelected = outreachStatus?.directInvestorOutreachEnabled ?? true
  const portfolioSelected = portfolioAvailable
  const reviewDone =
    (!investorSelected || outreachStatus?.messagesReviewedAt != null) &&
    (!portfolioSelected || portfolio?.messagesReviewedAt != null)

  const steps: LaunchStep[] = [
    {
      id: 'profile',
      label: 'Complete your profile',
      done: profilePercent >= PROFILE_COMPLETE_THRESHOLD,
    },
    {
      id: 'review',
      label: 'Review your messages',
      done: reviewDone,
    },
    {
      id: 'linkedin',
      label: 'Connect LinkedIn',
      done: isLinkedInConnected(outreachStatus),
    },
  ]

  const completedCount = steps.filter((s) => s.done).length
  const firstIncomplete = steps.find((s) => !s.done) ?? null
  const canCallLaunchMutation =
    outreachStatus?.status === 'AWAITING_CAMPAIGN' && outreachStatus.founderApprovedAt == null
  const allComplete = completedCount === steps.length && canCallLaunchMutation

  return {
    steps,
    completedCount,
    allComplete,
    firstIncomplete,
    profilePercent,
    profileFocus,
    canCallLaunchMutation,
  }
}
